/**
 * U1-VOCAB: the evaluators for the two declarative rule vocabularies the descriptor batches
 * asked for — `Param.aliasRules` (substring aliasing, U5) and `Param.domainRules` /
 * `ValuePredicate` (reject-by-predicate / per-surface domain, U4 and U6).
 *
 * PURE: `import type` + sibling modules only (purity.test). Importable from server/ and src/.
 * No RegExp: the purity allowlist forbids it, and it would also make a descriptor's domain
 * un-auditable. Substrings and the shared resolution ladder are the whole vocabulary.
 *
 * Both evaluators are TOTAL over every Scalar, which is the point: legacy classifies with
 * `String.prototype.includes` over a string the headless REST API forwards untouched, so any
 * rule a descriptor states about it must answer for every string, not for a finite list.
 */
import type { AliasRule, DomainRule, DomainRules, Param, Scalar, Surface, ValuePredicate } from './types';
import { legacyResolutionMultiplier } from './pricing';

/** True when the predicate states at least one condition (a keyless predicate never matches). */
export function predicateIsDefined(pred: ValuePredicate): boolean {
  return pred.eq !== undefined || pred.contains !== undefined
    || pred.containsLower !== undefined || pred.ladder !== undefined;
}

function ladderHolds(cmp: NonNullable<ValuePredicate['ladder']>, value: Scalar): boolean {
  const m = legacyResolutionMultiplier(typeof value === 'string' ? value : String(value));
  if (cmp.gt !== undefined && !(m > cmp.gt)) return false;
  if (cmp.gte !== undefined && !(m >= cmp.gte)) return false;
  if (cmp.lt !== undefined && !(m < cmp.lt)) return false;
  if (cmp.lte !== undefined && !(m <= cmp.lte)) return false;
  return true;
}

/**
 * Evaluates a ValuePredicate against one value. The present keys are a conjunction;
 * `negate: true` inverts the result of that conjunction. A predicate with no condition never
 * matches, negated or not — so `{ negate: true }` alone is not a catch-all accept.
 */
export function matchesPredicate(pred: ValuePredicate, value: Scalar): boolean {
  if (!predicateIsDefined(pred)) return false;
  const s = String(value);
  let hit = true;
  if (pred.eq !== undefined && s !== pred.eq) hit = false;
  if (hit && pred.contains !== undefined && !s.includes(pred.contains)) hit = false;
  if (hit && pred.containsLower !== undefined && !s.toLowerCase().includes(pred.containsLower)) hit = false;
  if (hit && pred.ladder !== undefined && !ladderHolds(pred.ladder, value)) hit = false;
  return pred.negate === true ? !hit : hit;
}

/** True when the alias rule states at least one condition (a keyless rule never matches). */
export function aliasRuleIsDefined(rule: AliasRule): boolean {
  return rule.eq !== undefined || rule.contains !== undefined || rule.containsLower !== undefined;
}

function aliasRuleMatches(rule: AliasRule, value: Scalar): boolean {
  if (!aliasRuleIsDefined(rule)) return false;
  const s = String(value);
  if (rule.eq !== undefined && s !== rule.eq) return false;
  if (rule.contains !== undefined && !s.includes(rule.contains)) return false;
  if (rule.containsLower !== undefined && !s.toLowerCase().includes(rule.containsLower)) return false;
  return true;
}

/** First matching rule's `to`, in declaration order; the value unchanged when none matches. */
export function applyAliasRules(rules: readonly AliasRule[] | undefined, value: Scalar): Scalar {
  if (!rules) return value;
  for (const rule of rules) if (aliasRuleMatches(rule, value)) return rule.to;
  return value;
}

const NO_RULES: readonly DomainRule[] = [];

/** The domain rules that apply on this surface (array form = both surfaces). */
export function domainRulesFor(p: Param, surface: Surface): readonly DomainRule[] {
  const rules: DomainRules | undefined = p.domainRules;
  if (!rules) return NO_RULES;
  if (Array.isArray(rules)) return rules as readonly DomainRule[];
  const bySurface = rules as { editor?: readonly DomainRule[]; headless?: readonly DomainRule[] };
  return bySurface[surface] ?? NO_RULES;
}

/** First domain rule of this surface whose predicate holds for `value`. */
export function matchDomainRule(p: Param, surface: Surface, value: Scalar): DomainRule | undefined {
  for (const rule of domainRulesFor(p, surface)) if (matchesPredicate(rule.when, value)) return rule;
  return undefined;
}
